<template>
  <!--
    Product switch, rendered as tabs.

    Deliberately NOT the panel treatment used by the bucket row on Leads and the
    pipeline on Offer Management. Choosing a product picks which book of business
    you are looking at; those panels filter within it. Two different acts should
    not look like the same control, and stacking three identical panels above a
    table made the page read as all filter and no content.

    Renders whatever `counts` it is handed, so the Dashboard can pass a third
    "All" entry while every other screen passes two -- no extra prop needed.
  -->
  <div class="kf-tabs">
    <button
      v-for="p in counts"
      :key="p.label"
      class="kf-tab"
      :class="{ 'kf-tab--active': value === p.label }"
      @click="select(p.label)"
    >
      <span class="kf-tab__label">{{ p.label }}</span>
      <span v-if="showCount" class="kf-tab__count">{{ fmt.number(p.count) }}</span>
    </button>

    <!-- Optional page-level action, right-aligned on this row.

         Screens with one primary button (Products' "Add product", the
         Dashboard's "Refresh") used to spend a full-width band above the tabs
         on it. This row already runs the width of the page and its tabs only
         reach about a third of the way across, so the button costs nothing
         here. Screens without one pass no slot and render exactly as before. -->
    <div v-if="$slots.action" class="kf-tabs__action">
      <slot name="action"></slot>
    </div>
  </div>
</template>

<script>
import fmt from "@/app-js/karrofin/format";

export default {
  name: "KfProductSwitch",
  props: {
    /** The selected product. One of PRODUCT_TYPES, or "All" on the Dashboard. */
    value: { type: String, required: true },
    /** [{ label, count }] -- both the tabs to draw and the numbers on them. */
    counts: { type: Array, default: () => [] },
    /** False for plain view tabs with no number (Notification History). On
        by default: a screen whose count is still loading shows "-" rather
        than a badge that pops in later. */
    showCount: { type: Boolean, default: true },
  },
  data() {
    return { fmt };
  },
  methods: {
    select(product) {
      // Nothing to fall back to: re-clicking the active tab is a no-op rather
      // than a toggle, because there is no unselected state.
      if (product === this.value) return;
      this.$emit("change", product);
    },
  },
};
</script>

<style scoped>
.kf-tabs {
  display: flex;
  gap: 2px;
  flex-wrap: wrap;
  border-bottom: 1px solid var(--kf-border);
  margin-bottom: 14px;
}
/* Left on the muted tier deliberately: this bar sits on the grey canvas, not on
   a white surface, and the active tab is brand navy -- a black inactive tab
   would look heavier than the selected one. */
.kf-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  /* The active underline sits ON the container's hairline, not above it. */
  border-bottom: 3px solid transparent;
  margin-bottom: -1px;
  font-size: 15px;
  font-weight: 600;
  color: var(--kf-text-muted);
  cursor: pointer;
  outline: none;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}
.kf-tab:hover:not(.kf-tab--active) {
  color: var(--kf-text-body);
  background: var(--kf-hover);
}
.kf-tab--active {
  color: var(--kf-brand);
  font-weight: 700;
  border-bottom-color: var(--kf-brand);
}
/*
  align-self is load-bearing, not cosmetic. .kf-tabs sets no align-items, so it
  defaults to `stretch`, and the tabs rely on that: each .kf-tab pairs a 3px
  bottom border with margin-bottom:-1px so its active underline lands exactly on
  the container's hairline. A stretched action wrapper would run that 1px past
  the hairline and read as a dent in the line. Centring this one item instead
  keeps it clear of the border and leaves every tab's stretch untouched.

  The row's height is unaffected: both callers' buttons (36px, and small
  outlined) are shorter than the 46px tabs, so the tabs still set the height.
*/
.kf-tabs__action {
  margin-left: auto;
  align-self: center;
  display: flex;
  align-items: center;
  gap: 8px;
}
.kf-tab__count {
  border-radius: 20px;
  padding: 1px 9px;
  font-size: var(--kf-fs-label);
  font-weight: 700;
  background: #dfe8f1;
  color: var(--kf-text-muted);
  transition: background 0.15s ease, color 0.15s ease;
}
.kf-tab--active .kf-tab__count {
  background: var(--kf-brand);
  color: #ffffff;
}
</style>
